import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

export interface PatientPickerItem {
  id: string;
  pat_number: string | null;
  first_name: string;
  last_name: string;
  email: string | null;
  phone: string | null;
}

export const patientPickerQueryKey = (clinicId: string | null, search: string) =>
  ['patient-picker', clinicId, search] as const;

/**
 * Lightweight patient typeahead used by the appointment modal. Reuses the
 * `search_tsv` FTS column from MOD-4. Returns empty list when search is empty
 * (we don't want to fetch all patients on first open).
 */
export function usePatientPicker(clinicId: string | null | undefined, search: string) {
  const trimmed = search.trim();
  return useQuery({
    queryKey: patientPickerQueryKey(clinicId ?? null, trimmed),
    enabled: Boolean(clinicId),
    queryFn: async (): Promise<PatientPickerItem[]> => {
      if (!clinicId) return [];

      let query = supabase
        .from('patients')
        .select('id, pat_number, first_name, last_name, email, phone')
        .eq('clinic_id', clinicId)
        .eq('status', 'active')
        .order('last_name', { ascending: true })
        .limit(20);

      if (trimmed) {
        if (trimmed.length <= 2) {
          const pattern = `%${trimmed}%`;
          query = query.or(
            `first_name.ilike.${pattern},last_name.ilike.${pattern},pat_number.ilike.${pattern}`,
          );
        } else {
          const tsQuery = trimmed
            .split(/\s+/)
            .filter((t) => t.length > 0)
            .map((t) => `${t}:*`)
            .join(' & ');
          query = query.textSearch('search_tsv', tsQuery, { config: 'simple' });
        }
      }

      const { data, error } = await query;
      if (error) throw error;
      return data ?? [];
    },
    staleTime: 30_000,
  });
}
